// Capture routes for move input (spec/move-input-spec.md §2, §3): every legal
// move with all its landing routes, the piece jumped on each step, and the
// small set operations the input handler narrows candidates with.
import type { LegalMove } from "../api/client";
import { rowCol, squareAt } from "../lib/textfen";

/** One landing route of one legal move. `sq` is origin then every landing,
 * `caps[i]` the piece jumped between `sq[i]` and `sq[i + 1]` (empty for a
 * quiet move). Two routes of the same move share the `move` object. */
export type Route = { move: LegalMove; sq: number[]; caps: number[] };

export type Content = "own" | "opp" | "empty";

export function contentOf(textfen: string, sq: number): Content {
  const c = textfen[sq];
  if (c === "_" || c === undefined) return "empty";
  const white = c === "w" || c === "W";
  return white === (textfen[0] === "W") ? "own" : "opp";
}

/** The squares strictly between two squares on one diagonal (none if not on one). */
export function between(a: number, b: number): number[] {
  const [r1, c1] = rowCol(a);
  const [r2, c2] = rowCol(b);
  const dr = Math.sign(r2 - r1);
  const dc = Math.sign(c2 - c1);
  if (Math.abs(r2 - r1) !== Math.abs(c2 - c1) || dr === 0) return [];
  const out: number[] = [];
  for (let r = r1 + dr, c = c1 + dc; r !== r2; r += dr, c += dc) out.push(squareAt(r, c)!);
  return out;
}

/** Are b and c both on the same diagonal ray out of a (in either order)? */
export function onRay(a: number, b: number, c: number): boolean {
  return inLine(a, b, c) || inLine(a, c, b);
}

/** Are a, b and c on one diagonal, in that order? */
export function inLine(a: number, b: number, c: number): boolean {
  const [r1, c1] = rowCol(a);
  const [r2, c2] = rowCol(b);
  const [r3, c3] = rowCol(c);
  const d1 = [Math.sign(r2 - r1), Math.sign(c2 - c1)];
  const d2 = [Math.sign(r3 - r2), Math.sign(c3 - c2)];
  return between(a, c).includes(b) && d1[0] === d2[0] && d1[1] === d2[1];
}

function routesOf(m: LegalMove): number[][] {
  if (m.routes && m.routes.length) return m.routes;
  if (m.path && m.path.length >= 2) return [m.path];
  return [[m.from, m.to]];
}

/** All routes of all legal moves, sorted by their squares. */
export function buildRoutes(textfen: string, legal: LegalMove[]): Route[] {
  const out: Route[] = [];
  for (const move of legal) {
    for (const sq of routesOf(move)) {
      const caps = move.captured.length
        ? sq.slice(1).map((to, i) => between(sq[i], to).find((s) => contentOf(textfen, s) === "opp") ?? 0)
        : [];
      out.push({ move, sq, caps });
    }
  }
  return out.sort((a, b) => cmp(a.sq, b.sq));
}

function cmp(a: number[], b: number[]): number {
  for (let i = 0; i < Math.min(a.length, b.length); i++) if (a[i] !== b[i]) return a[i] - b[i];
  return a.length - b.length;
}

export function movesOf(routes: Route[]): LegalMove[] {
  return [...new Set(routes.map((r) => r.move))];
}

/** The longest common beginning of the routes' squares. */
export function commonPrefix(routes: Route[]): number[] {
  if (!routes.length) return [];
  const first = routes[0].sq;
  let n = first.length;
  for (const r of routes) {
    let i = 0;
    while (i < n && i < r.sq.length && r.sq[i] === first[i]) i++;
    n = i;
  }
  return first.slice(0, n);
}

export function startsWith(r: Route, prefix: number[]): boolean {
  return prefix.length <= r.sq.length && prefix.every((s, i) => r.sq[i] === s);
}

/** Pieces jumped while walking `prefix` (all routes that share it agree). */
export function jumped(routes: Route[], prefix: number[]): number[] {
  const r = routes.find((x) => startsWith(x, prefix));
  return r ? r.caps.slice(0, Math.max(0, prefix.length - 1)) : [];
}

/**
 * Could the piece on `at` jump to `to` over one opponent's piece not yet
 * taken, with nothing else in the way? Used for the "most pieces" hint (H2):
 * the jump is legal on its own but not part of a maximum capture.
 */
export function isJump(
  textfen: string,
  at: number,
  to: number,
  taken: number[],
  king: boolean,
  vacated: number,
): boolean {
  const free = (s: number) => s === vacated || contentOf(textfen, s) === "empty";
  const line = between(at, to);
  if (!line.length || !free(to)) return false;
  const opp = line.filter((s) => contentOf(textfen, s) === "opp");
  if (opp.length !== 1 || taken.includes(opp[0])) return false;
  if (line.some((s) => s !== opp[0] && !free(s))) return false;
  // a man jumps the piece next to it; a king may fly before and after it
  return king || line.length === 1;
}
